/*
Copyright 2023, 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  type FC,
  type MouseEvent,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Button, Text } from "@vector-im/compound-web";
import { PopOutIcon } from "@vector-im/compound-design-tokens/assets/web/icons";

import { useClient } from "../ClientContext";
import { Modal } from "../Modal";
import { useRoomEncryptionSystem } from "../e2ee/roomEncryption";
import { getAbsoluteRoomUrl } from "../utils/matrix";
import styles from "./AppSelectionModal.module.css";
import { editFragmentQuery } from "../UrlParams";
import { getHomeserverAssociatedDomain } from "../knownHomeserversMap";
import { Config } from "../config/Config";

interface Props {
  /** Где открыта страница: от этого зависит, какую ссылку предлагать. */
  platform: string;
  roomId: string;
}

/**
 * Строит ссылку для открытия комнаты в приложении Klava.
 * Для известных homeserver'ов переводит ссылку на web-домен,
 * где уже настроены universal/app links мобильных клиентов.
 */
export function buildAppSelectionUrl(
  browserRoomUrl: string,
  homeserver?: string,
): string {
  const url = new URL(browserRoomUrl);

  if (homeserver) {
    const webDomain = getHomeserverAssociatedDomain(homeserver, "web");

    if (webDomain !== window.location.host) {
      url.protocol = "https:";
      url.host = webDomain;
      url.port = "";
    }
  }

  return url.toString();
}

export const AppSelectionModal: FC<Props> = ({ roomId, platform }) => {
  const { t } = useTranslation();
  const { client } = useClient();

  const [open, setOpen] = useState(true);
  const onBrowserClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    },
    [setOpen],
  );
  const e2eeSystem = useRoomEncryptionSystem(roomId);
  const homeserver = client?.getDomain() ?? Config.defaultServerName();

  const appUrl = useMemo(() => {
    const url = new URL(getAbsoluteRoomUrl(roomId, e2eeSystem));
    // Отключаем повторный prompt внутри приложения и фиксируем пользователя в комнате.
    url.hash = editFragmentQuery(url.hash, (params) => {
      params.set("appPrompt", "false");
      params.set("confineToRoom", "true");
      return params;
    });

    const scheme = Config.get().desktop_app_scheme;
    if (platform === "desktop" && scheme) {
      // На компьютере универсальных ссылок нет: приложение ловит только свою схему
      // и переносит из ссылки якорь. Поэтому отдаём ему сразу адрес комнаты —
      // звонок живёт внутри неё, и клиент откроет его сам.
      const inApp = new URL(`${scheme}://vector/webapp/`);
      inApp.hash = `#/room/${roomId}`;
      return inApp.toString();
    }

    return buildAppSelectionUrl(url.toString(), homeserver);
  }, [e2eeSystem, homeserver, platform, roomId]);

  return (
    <Modal
      className={styles.modal}
      title={t("app_selection_modal.title")}
      open={open}
    >
      <Text size="md" weight="semibold">
        {t("app_selection_modal.text")}
      </Text>
      <Button kind="secondary" onClick={onBrowserClick}>
        {t("app_selection_modal.continue_in_browser")}
      </Button>
      <Button as="a" href={appUrl} Icon={PopOutIcon}>
        {t("app_selection_modal.open_in_app")}
      </Button>
    </Modal>
  );
};
